import type { Trip, TripDay, TripStop } from "../types";
import { setDayCity } from "../lib/trip-route";
import { pruneStays, setLegStay } from "../lib/trip-stays";
import type { TripStayRef } from "../types";

export interface TripState {
  trips: readonly Trip[];
  activeTripId: string | null;
  onboardingStep: 1 | 2 | 3 | null;
  addStopOpen: boolean;
  addStopDayId: string | null;
}

export type TripAction =
  | { type: "HYDRATE"; trips: readonly Trip[]; activeTripId: string | null }
  | { type: "CREATE_TRIP"; trip: Trip }
  | { type: "REPLACE_TRIP"; trip: Trip }
  | { type: "DELETE_TRIP"; tripId: string }
  | { type: "SET_ACTIVE_TRIP"; tripId: string | null }
  | { type: "RENAME_TRIP"; tripId: string; name: string }
  | { type: "SET_DAY_CITY"; tripId: string; dayId: string; city: string }
  | { type: "SET_LEG_STAY"; tripId: string; nightDayIds: readonly string[]; stay: TripStayRef | null }
  | { type: "ADD_DAY"; tripId: string; day: TripDay }
  | { type: "REMOVE_DAY"; tripId: string; dayId: string }
  | { type: "ADD_STOP"; tripId: string; dayId: string; stop: TripStop }
  | { type: "REMOVE_STOP"; tripId: string; dayId: string; stopId: string }
  | { type: "REORDER_STOPS"; tripId: string; dayId: string; stopIds: readonly string[] }
  | {
      type: "MOVE_STOP";
      tripId: string;
      fromDayId: string;
      toDayId: string;
      stopId: string;
    }
  | {
      type: "UPDATE_STOP_TIME";
      tripId: string;
      dayId: string;
      stopId: string;
      startTime: string | undefined;
      endTime: string | undefined;
    }
  | {
      type: "UPDATE_STOP_NOTE";
      tripId: string;
      dayId: string;
      stopId: string;
      note: string | undefined;
    }
  | { type: "SORT_DAY_BY_TIME"; tripId: string; dayId: string }
  | { type: "SET_AIRPORT_TRANSFER"; tripId: string; enabled: boolean }
  | { type: "OPEN_ONBOARDING" }
  | { type: "ADVANCE_ONBOARDING" }
  | { type: "CLOSE_ONBOARDING" }
  | { type: "OPEN_ADD_STOP"; dayId: string }
  | { type: "CLOSE_ADD_STOP" };

export function createInitialTripState(): TripState {
  return {
    trips: [],
    activeTripId: null,
    onboardingStep: null,
    addStopOpen: false,
    addStopDayId: null,
  };
}

function mapTrip(
  state: TripState,
  tripId: string,
  transform: (trip: Trip) => Trip,
): TripState {
  return {
    ...state,
    trips: state.trips.map((trip) => (trip.id === tripId ? transform(trip) : trip)),
  };
}

function mapDay(trip: Trip, dayId: string, transform: (day: TripDay) => TripDay): Trip {
  return {
    ...trip,
    days: trip.days.map((day) => (day.id === dayId ? transform(day) : day)),
  };
}

export function tripReducer(state: TripState, action: TripAction): TripState {
  switch (action.type) {
    case "HYDRATE":
      return { ...state, trips: action.trips, activeTripId: action.activeTripId };
    case "CREATE_TRIP":
      return {
        ...state,
        trips: [...state.trips, action.trip],
        activeTripId: action.trip.id,
      };
    case "REPLACE_TRIP":
      return {
        ...state,
        trips: state.trips.map((trip) =>
          trip.id === action.trip.id ? action.trip : trip,
        ),
      };
    case "DELETE_TRIP":
      return {
        ...state,
        trips: state.trips.filter((trip) => trip.id !== action.tripId),
        activeTripId:
          state.activeTripId === action.tripId ? null : state.activeTripId,
      };
    case "SET_ACTIVE_TRIP":
      return { ...state, activeTripId: action.tripId };
    case "RENAME_TRIP":
      return mapTrip(state, action.tripId, (trip) => ({ ...trip, name: action.name }));
    case "SET_DAY_CITY":
      // A day moved to another city keeps no hotel from the old one.
      return mapTrip(state, action.tripId, (trip) => pruneStays(setDayCity(trip, action.dayId, action.city)));
    case "SET_LEG_STAY":
      return mapTrip(state, action.tripId, (trip) => setLegStay(trip, action.nightDayIds, action.stay));
    case "ADD_DAY":
      return mapTrip(state, action.tripId, (trip) => {
        // A new day carries on where the trip ends: the last day's city.
        const last = trip.days[trip.days.length - 1];
        const day =
          action.day.city || !last?.city ? action.day : { ...action.day, city: last.city };
        // The old last day was the day you leave; now it ends with a night.
        // In the same city it is spent where the night before was.
        const before = trip.days[trip.days.length - 2];
        const days =
          last && !last.stay && before?.stay && (before.city ?? trip.city) === (last.city ?? trip.city) &&
          (day.city ?? trip.city) === (last.city ?? trip.city)
            ? [...trip.days.slice(0, -1), { ...last, stay: before.stay }]
            : trip.days;
        return { ...trip, days: [...days, day] };
      });
    case "REMOVE_DAY":
      return mapTrip(state, action.tripId, (trip) => {
        const days = trip.days.filter((day) => day.id !== action.dayId);
        // `city` always names day 1's city (see lib/trip-route.ts).
        return { ...trip, city: days[0]?.city || trip.city, days };
      });
    case "ADD_STOP":
      return mapTrip(state, action.tripId, (trip) =>
        mapDay(trip, action.dayId, (day) => ({
          ...day,
          stops: [...day.stops, action.stop],
        })),
      );
    case "REMOVE_STOP":
      return mapTrip(state, action.tripId, (trip) =>
        mapDay(trip, action.dayId, (day) => ({
          ...day,
          stops: day.stops.filter((stop) => stop.id !== action.stopId),
        })),
      );
    case "REORDER_STOPS":
      return mapTrip(state, action.tripId, (trip) =>
        mapDay(trip, action.dayId, (day) => {
          const byId = new Map(day.stops.map((stop) => [stop.id, stop]));
          const reordered = action.stopIds
            .map((id) => byId.get(id))
            .filter((stop): stop is TripStop => stop !== undefined);
          return { ...day, stops: reordered };
        }),
      );
    case "UPDATE_STOP_TIME":
      return mapTrip(state, action.tripId, (trip) =>
        mapDay(trip, action.dayId, (day) => ({
          ...day,
          stops: day.stops.map((stop) =>
            stop.id === action.stopId
              ? {
                  ...stop,
                  startTime: action.startTime,
                  endTime: action.endTime,
                }
              : stop,
          ),
        })),
      );
    case "UPDATE_STOP_NOTE":
      return mapTrip(state, action.tripId, (trip) =>
        mapDay(trip, action.dayId, (day) => ({
          ...day,
          stops: day.stops.map((stop) =>
            stop.id === action.stopId ? { ...stop, note: action.note } : stop,
          ),
        })),
      );
    case "SORT_DAY_BY_TIME":
      return mapTrip(state, action.tripId, (trip) =>
        mapDay(trip, action.dayId, (day) => {
          // Stable sort: stops with a startTime sort chronologically;
          // stops without one keep their relative order and sink to
          // the bottom of the list.
          const indexed = day.stops.map((stop, index) => ({ stop, index }));
          indexed.sort((a, b) => {
            const aTime = a.stop.startTime;
            const bTime = b.stop.startTime;
            if (aTime && bTime) return aTime.localeCompare(bTime);
            if (aTime && !bTime) return -1;
            if (!aTime && bTime) return 1;
            return a.index - b.index;
          });
          return { ...day, stops: indexed.map((e) => e.stop) };
        }),
      );
    case "SET_AIRPORT_TRANSFER":
      return mapTrip(state, action.tripId, (trip) => ({
        ...trip,
        airportTransfer: action.enabled,
      }));
    case "MOVE_STOP": {
      // Cross-day move: pluck the stop out of fromDayId and append it
      // to the end of toDayId. Same-day calls become a no-op so the
      // caller doesn't need to pre-check.
      if (action.fromDayId === action.toDayId) return state;
      return mapTrip(state, action.tripId, (trip) => {
        const source = trip.days.find((d) => d.id === action.fromDayId);
        const stop = source?.stops.find((s) => s.id === action.stopId);
        if (!stop) return trip;
        return {
          ...trip,
          days: trip.days.map((day) => {
            if (day.id === action.fromDayId) {
              return {
                ...day,
                stops: day.stops.filter((s) => s.id !== action.stopId),
              };
            }
            if (day.id === action.toDayId) {
              return { ...day, stops: [...day.stops, stop] };
            }
            return day;
          }),
        };
      });
    }
    case "OPEN_ONBOARDING":
      return { ...state, onboardingStep: 1 };
    case "ADVANCE_ONBOARDING":
      return {
        ...state,
        onboardingStep:
          state.onboardingStep === null
            ? 1
            : state.onboardingStep < 3
              ? ((state.onboardingStep + 1) as 2 | 3)
              : state.onboardingStep,
      };
    case "CLOSE_ONBOARDING":
      return { ...state, onboardingStep: null };
    case "OPEN_ADD_STOP":
      return { ...state, addStopOpen: true, addStopDayId: action.dayId };
    case "CLOSE_ADD_STOP":
      return { ...state, addStopOpen: false, addStopDayId: null };
    default:
      return state;
  }
}
